import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { TeamLogo } from "@/components/TeamLogo";
import { LeagueChip } from "@/components/LeagueLogo";
import { Trophy } from "lucide-react";

const LEAGUES = ["All", "NBA", "NFL", "NHL", "MLB", "NCAAB", "EPL"];

export default function Rankings() {
  const [league, setLeague] = useState("All");
  const [teams, setTeams] = useState([]);

  useEffect(() => {
    const params = league === "All" ? { limit: 30 } : { league, limit: 30 };
    api.get("/dashboard/rankings", { params }).then((r) => setTeams(r.data));
  }, [league]);

  const maxElo = teams[0]?.elo ?? 1700;
  const minElo = teams[teams.length - 1]?.elo ?? 1400;

  return (
    <div className="mx-auto max-w-5xl space-y-6" data-testid="rankings-page">
      <PageHeader
        title="ELO power rankings"
        subtitle="Self-learning ratings calibrated from historical outcomes and margin of victory."
        actions={
          <div className="flex items-center gap-1 rounded-lg border border-border bg-gray-0 p-1">
            {LEAGUES.map((l) => (
              <button
                key={l}
                onClick={() => setLeague(l)}
                data-testid={`rankings-league-${l.toLowerCase()}`}
                className={`rounded-md px-3 py-1.5 text-xs font-semibold transition-all ${
                  league === l ? "bg-primary text-primary-foreground" : "text-gray-500 hover:text-gray-900"
                }`}
              >
                {l}
              </button>
            ))}
          </div>
        }
      />

      <div className="iso-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left">
            <tr>
              <th className="px-5 py-3 text-[10px] font-semibold uppercase tracking-wider text-gray-500">#</th>
              <th className="px-5 py-3 text-[10px] font-semibold uppercase tracking-wider text-gray-500">Team</th>
              <th className="px-5 py-3 text-[10px] font-semibold uppercase tracking-wider text-gray-500">League</th>
              <th className="px-5 py-3 text-[10px] font-semibold uppercase tracking-wider text-gray-500">Record</th>
              <th className="px-5 py-3 text-[10px] font-semibold uppercase tracking-wider text-gray-500">ELO</th>
              <th className="px-5 py-3 text-[10px] font-semibold uppercase tracking-wider text-gray-500">Tier</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {teams.map((t, i) => {
              const tier = tierFor(t.elo);
              const pct = ((t.elo - minElo) / Math.max(1, maxElo - minElo)) * 100;
              return (
                <tr key={t.id} className="hover:bg-gray-50" data-testid={`rankings-row-${i}`}>
                  <td className="px-5 py-3 font-lexend text-sm font-bold text-gray-400">{i + 1}</td>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      <TeamLogo src={t.logo} abbr={t.abbr} color={t.color} size={32} />
                      <div className="font-semibold text-gray-900">{t.name}</div>
                    </div>
                  </td>
                  <td className="px-5 py-3"><LeagueChip code={t.league} /></td>
                  <td className="px-5 py-3 font-mono text-xs text-gray-600">{t.record_w}-{t.record_l}</td>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      <span className="font-lexend text-base font-bold text-gray-900">{t.elo.toFixed(0)}</span>
                      <div className="h-1.5 w-24 overflow-hidden rounded-full bg-gray-100">
                        <div className="h-full rounded-full bg-secondary" style={{ width: `${Math.max(8, pct)}%` }} />
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3">
                    <span className={`iso-chip ${tier.chip}`}>
                      <Trophy className="size-3" /> {tier.label}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function tierFor(elo) {
  if (elo >= 1650) return { label: "Elite", chip: "iso-chip-green" };
  if (elo >= 1580) return { label: "Contender", chip: "iso-chip-blue" };
  if (elo >= 1520) return { label: "Solid", chip: "iso-chip-secondary" };
  return { label: "Rebuilding", chip: "" };
}
